// src/pages/PrivacyPage.jsx
import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10">
    <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-slate-800">{title}</h2>
    <div className="space-y-3 text-slate-400 text-sm leading-relaxed">{children}</div>
  </section>
);

const DataRow = ({ label, value }) => (
  <div className="grid grid-cols-5 gap-4 py-3 border-b border-slate-800/60 last:border-0">
    <div className="col-span-2 text-slate-300 text-sm font-medium">{label}</div>
    <div className="col-span-3 text-slate-400 text-sm">{value}</div>
  </div>
);

const PrivacyPage = () => {
  const EFFECTIVE_DATE = "March 13, 2026";
  const COMPANY = "AI Workshop LLC";
  const EMAIL = "support@aiworkshopllc.com";
  const SITE = "aiworkshopllc.com";

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <Helmet>
        <title>Privacy Policy � AI Workshop LLC</title>
        <meta name="description" content="Privacy Policy for AI Workshop LLC. How we collect, use, and protect your business and personal information." />
      </Helmet>
      <Header />

      <div className="max-w-3xl mx-auto px-4 py-20">
        {/* Header */}
        <div className="mb-12">
          <div className="text-xs text-slate-500 uppercase tracking-widest font-semibold mb-3">Legal</div>
          <h1 className="text-4xl font-black text-white mb-4">Privacy Policy</h1>
          <p className="text-slate-500 text-sm">Effective date: {EFFECTIVE_DATE}</p>
          <p className="text-slate-400 text-sm mt-3">
            {COMPANY} ("we," "us," or "our") operates {SITE} and is committed to protecting your privacy. This Privacy Policy explains what information we collect, why we collect it, how we use it, and your rights regarding your information. Please read this policy carefully before using our services.
          </p>
        </div>

        {/* TOC */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 mb-12">
          <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold mb-4">Contents</p>
          <ol className="space-y-1.5 text-sm text-slate-400">
            {[
              ["1", "Information We Collect"],
              ["2", "How We Use Your Information"],
              ["3", "How We Share Your Information"],
              ["4", "Third-Party Service Providers"],
              ["5", "AI and LLM Processing"],
              ["6", "Cookies and Tracking Technologies"],
              ["7", "Email Communications"],
              ["8", "Data Retention"],
              ["9", "Data Security"],
              ["10", "Children's Privacy"],
              ["11", "Your Rights (CCPA / GDPR)"],
              ["12", "California Residents"],
              ["13", "EEA and UK Residents"],
              ["14", "Changes to This Policy"],
              ["15", "Contact Us"],
            ].map(([num, label]) => (
              <li key={num}>
                <a href={`#section-${num}`} className="hover:text-[#3b82f6] transition-colors">
                  {num}. {label}
                </a>
              </li>
            ))}
          </ol>
        </div>

        <Section id="section-1" title="1. Information We Collect">
          <p><strong className="text-white">Information you provide directly</strong></p>
          <p>When you use our service, you provide us with business and contact information through the audit intake form. This includes:</p>
          <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden mt-3 mb-3">
            <DataRow label="Business name" value="Name of your business or organization" />
            <DataRow label="Industry" value="Your business category (e.g., restaurant, retail, healthcare)" />
            <DataRow label="Business size" value="Number of employees" />
            <DataRow label="Monthly revenue" value="Approximate revenue range (optional)" />
            <DataRow label="Monthly AI budget" value="What you're willing to spend on AI tools" />
            <DataRow label="Current tools" value="Software and tools your business currently uses" />
            <DataRow label="Business problems" value="Challenges you want AI to help solve (free text)" />
            <DataRow label="Goals" value="What you want to achieve with AI (free text)" />
            <DataRow label="Email address" value="Used to deliver your report and send follow-up emails" />
            <DataRow label="Business type" value="Subcategory (e.g., e-commerce, SaaS, brick-and-mortar)" />
          </div>
          <p>
            If you subscribe to our monthly service, we also collect: subscription status, billing cycle dates, and Stripe customer identifiers (not your full card number � that stays with Stripe).
          </p>
          <p>
            If you apply for Agency API access, we collect: agency name, contact email, and intended use.
          </p>

          <p className="mt-4"><strong className="text-white">Information collected automatically</strong></p>
          <p>When you visit our website, we automatically collect basic technical information, including:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Browser type and version</li>
            <li>Device type and operating system</li>
            <li>IP address (used for fraud prevention and geographic analytics only)</li>
            <li>Pages visited, time on page, and referral source (via Google Analytics, if enabled)</li>
            <li>URL query parameters (e.g., industry pre-fill from landing page links)</li>
          </ul>

          <p className="mt-4"><strong className="text-white">Information we do NOT collect</strong></p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Full payment card numbers (processed entirely by Stripe)</li>
            <li>Social Security numbers or government IDs</li>
            <li>Sensitive health information</li>
            <li>Passwords (we use email-based authentication only)</li>
          </ul>
        </Section>

        <Section id="section-2" title="2. How We Use Your Information">
          <p>We use the information you provide for the following purposes:</p>
          <div className="space-y-4 mt-2">
            {[
              {
                title: "Generating your Blueprint",
                desc: "Your intake form data is the primary input for generating your personalized AI Implementation Blueprint. We process your industry, problems, tools, budget, and revenue to produce tool recommendations, ROI projections, and your implementation roadmap.",
              },
              {
                title: "Delivering your report",
                desc: "Your email address is used to send your PDF Blueprint, report access link, and payment confirmation.",
              },
              {
                title: "Follow-up email sequences",
                desc: "After purchase, we send automated follow-up emails at Day 3, Day 7, and Day 90 to help you implement your plan. These include your top tool recommendation and personalized implementation tips. You can opt out at any time.",
              },
              {
                title: "Subscription management",
                desc: "For subscribers, we use your email and subscription data to deliver weekly digest emails, manage billing, and send subscription status notifications.",
              },
              {
                title: "Improving our service",
                desc: "We aggregate and anonymize intake data to improve our tool database, scoring models, and benchmark data. No individual business is identifiable in this analysis.",
              },
              {
                title: "Fraud prevention and security",
                desc: "We use technical information (IP address, browser data) to detect and prevent fraudulent activity, abuse of promo codes, or unauthorized API usage.",
              },
              {
                title: "Customer support",
                desc: "When you contact us, we use your information to respond to your inquiry and resolve issues.",
              },
            ].map((item) => (
              <div key={item.title} className="bg-slate-900/40 border border-slate-800 rounded-xl p-4">
                <p className="text-white font-semibold text-sm mb-1">{item.title}</p>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section id="section-3" title="3. How We Share Your Information">
          <p>
            We do not sell your personal information to third parties. We do not share your personal information with advertisers or data brokers.
          </p>
          <p>We share your information only in the following limited circumstances:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong className="text-slate-300">Service providers:</strong> We share data with the third-party providers listed in Section 4, solely to operate our service.</li>
            <li><strong className="text-slate-300">AI processing:</strong> Your business intake data is sent to LLM providers (see Section 5) to generate report content. This is a core function of our service.</li>
            <li><strong className="text-slate-300">Agency resellers:</strong> If you submit an intake form through a white-label partner's platform, the agency partner may receive your report and basic contact information as part of the service they provide. We require agency partners to maintain appropriate privacy protections.</li>
            <li><strong className="text-slate-300">Legal requirements:</strong> We may disclose your information if required by law, regulation, legal process, or governmental request.</li>
            <li><strong className="text-slate-300">Business transfers:</strong> If {COMPANY} is acquired or merges with another entity, your information may be transferred as part of that transaction, subject to comparable privacy protections.</li>
            <li><strong className="text-slate-300">Protection of rights:</strong> We may share information to enforce our Terms of Service, protect our rights, or prevent harm.</li>
          </ul>
        </Section>

        <Section id="section-4" title="4. Third-Party Service Providers">
          <p>We use the following third-party providers to operate our service. Each has their own privacy policy governing their use of data.</p>
          <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden mt-3">
            <div className="grid grid-cols-5 gap-4 px-5 py-3 bg-slate-950/50">
              <div className="col-span-2 text-slate-300 text-xs font-bold uppercase tracking-wide">Provider</div>
              <div className="col-span-3 text-slate-300 text-xs font-bold uppercase tracking-wide">Purpose / Data Shared</div>
            </div>
            {[
              ["Stripe, Inc.", "Payment processing. Receives payment card data, billing email, and transaction amount. Stripe stores card data � we do not."],
              ["Resend", "Transactional email delivery. Receives your email address and email content (report links, PDFs, follow-up sequences)."],
              ["MongoDB Atlas (MongoDB, Inc.)", "Cloud database. Stores your intake form data, report content, and email schedule. Data is encrypted at rest and in transit."],
              ["xAI (Grok)", "LLM provider. Receives anonymized business context from your intake form to generate report content. See Section 5."],
              ["Anthropic (Claude)", "LLM provider used for Ask AI Q&A feature. Receives your question and relevant report context. See Section 5."],
              ["Google Analytics", "Website analytics. Collects anonymized page view data, referral sources, and user behavior. Does not receive your form data."],
              ["Render", "Backend infrastructure hosting. Processes requests on our servers. Does not independently store your data."],
            ].map(([provider, purpose]) => (
              <DataRow key={provider} label={provider} value={purpose} />
            ))}
          </div>
        </Section>

        <Section id="section-5" title="5. AI and LLM Processing">
          <p>
            A core feature of our service is using large language model (LLM) AI to generate personalized sections of your Blueprint. When you submit your intake form and pay for a report, relevant portions of your business information � including industry, problems, goals, and tool usage � are sent to xAI's Grok API and/or Anthropic's Claude API.
          </p>
          <p>
            <strong className="text-white">What is sent to LLM providers:</strong> Industry, business size, stated problems, goals, and current tools. We do not send your personal name, email address, or payment information to LLM providers.
          </p>
          <p>
            <strong className="text-white">How it's used:</strong> LLM providers process this information to help generate report sections including the executive summary, competitive intelligence narrative, and custom prompt library. The output is then stored in our database and delivered to you as part of your report.
          </p>
          <p>
            <strong className="text-white">LLM provider data practices:</strong> xAI and Anthropic have their own privacy and data usage policies. We select API providers who commit to not using API-submitted data to train their models by default. However, you should be aware that business information shared for report generation does leave our systems and is processed by these third parties.
          </p>
          <p>
            If you prefer not to have your information processed by LLM providers, you may contact us before purchase and request a rule-engine-only report, which will omit LLM-generated narrative sections.
          </p>
        </Section>

        <Section id="section-6" title="6. Cookies and Tracking Technologies">
          <p>
            Our website uses cookies and similar technologies for the following purposes:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong className="text-slate-300">Essential cookies:</strong> Required for the website to function. Include session management and authentication tokens. Cannot be disabled.</li>
            <li><strong className="text-slate-300">Functional cookies:</strong> Remember your preferences (e.g., language selection, saved form data via localStorage). Stored locally in your browser.</li>
            <li><strong className="text-slate-300">Analytics cookies:</strong> Google Analytics cookies track page views and referral sources in aggregate. This data is anonymized and does not identify you individually.</li>
          </ul>
          <p>
            <strong className="text-white">Form data in localStorage:</strong> To improve your experience, your audit form progress is saved in your browser's localStorage. This data stays entirely on your device and is not transmitted to our servers until you submit the form.
          </p>
          <p>
            You can control cookies through your browser settings. Disabling analytics cookies will not affect your ability to use our service. Disabling essential cookies may impair functionality.
          </p>
        </Section>

        <Section id="section-7" title="7. Email Communications">
          <p>
            By providing your email address and purchasing a Blueprint, you consent to receive:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Your report delivery email (with PDF attachment and report link)</li>
            <li>Payment confirmation</li>
            <li>Automated implementation follow-up emails at Day 3, Day 7, and Day 90 after purchase</li>
            <li>Milestone emails triggered by your implementation progress (if you use the progress tracker)</li>
            <li>Weekly industry digest (if you are a subscriber)</li>
          </ul>
          <p>
            <strong className="text-white">Opting out:</strong> Every email we send includes an unsubscribe link. You can unsubscribe from follow-up and marketing emails at any time. Transactional emails (report delivery, payment confirmation, subscription billing) cannot be opted out of while you have an active account or subscription.
          </p>
          <p>
            We do not send unsolicited commercial email to people who have not interacted with our service.
          </p>
        </Section>

        <Section id="section-8" title="8. Data Retention">
          <p>We retain your data for the following periods:</p>
          <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden mt-3">
            <div className="grid grid-cols-5 gap-4 px-5 py-3 bg-slate-950/50">
              <div className="col-span-2 text-slate-300 text-xs font-bold uppercase tracking-wide">Data Type</div>
              <div className="col-span-3 text-slate-300 text-xs font-bold uppercase tracking-wide">Retention Period</div>
            </div>
            {[
              ["Audit intake form data", "Indefinitely, or until you request deletion. Used to regenerate or update your report."],
              ["Your generated Blueprint", "Indefinitely, or until you request deletion."],
              ["Payment transaction records", "7 years (required for tax and financial compliance)."],
              ["Email logs", "2 years."],
              ["Subscription data", "Active subscription period + 90 days after cancellation, then deleted."],
              ["Failed/unpaid intake submissions", "30 days, then automatically purged."],
              ["Website analytics (Google Analytics)", "26 months (Google's default retention)."],
            ].map(([type, period]) => (
              <DataRow key={type} label={type} value={period} />
            ))}
          </div>
          <p>
            You may request deletion of your personal data at any time (see Section 11). Payment transaction records are retained for tax compliance reasons even after deletion requests.
          </p>
        </Section>

        <Section id="section-9" title="9. Data Security">
          <p>
            We implement industry-standard security measures to protect your information:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>All data in transit is encrypted using TLS/HTTPS</li>
            <li>Database data is encrypted at rest via MongoDB Atlas</li>
            <li>API keys and secrets are stored in environment variables, not in code</li>
            <li>Payment card data is never stored on our servers � handled exclusively by Stripe (PCI DSS Level 1 certified)</li>
            <li>Access to our database is restricted by IP allowlist and credential-based authentication</li>
          </ul>
          <p>
            Despite these measures, no system is completely secure. We cannot guarantee absolute security of data transmitted over the internet. If you believe your information has been compromised, contact us immediately at {EMAIL}.
          </p>
          <p>
            In the event of a data breach affecting your personal information, we will notify you as required by applicable law.
          </p>
        </Section>

        <Section id="section-10" title="10. Children's Privacy">
          <p>
            Our services are not directed to, and we do not knowingly collect personal information from, anyone under the age of 18. If we become aware that we have collected personal information from a minor, we will promptly delete it. If you believe a minor has provided us with personal information, please contact us at {EMAIL}.
          </p>
        </Section>

        <Section id="section-11" title="11. Your Rights">
          <p>Depending on your location, you may have the following rights regarding your personal information:</p>
          <div className="space-y-3 mt-2">
            {[
              { right: "Access", desc: "Request a copy of the personal information we hold about you." },
              { right: "Correction", desc: "Request that we correct inaccurate or incomplete personal information." },
              { right: "Deletion", desc: "Request deletion of your personal information. Note: payment transaction records are retained for legal compliance." },
              { right: "Opt out of emails", desc: "Unsubscribe from non-transactional email communications at any time via the unsubscribe link in any email." },
              { right: "Data portability", desc: "Request your intake form data and report in a machine-readable format." },
              { right: "Withdraw consent", desc: "Where processing is based on consent, withdraw consent at any time without affecting prior processing." },
            ].map((item) => (
              <div key={item.right} className="flex gap-3">
                <div className="w-2 h-2 rounded-full bg-teal-400 flex-shrink-0 mt-1.5" />
                <div>
                  <span className="text-slate-300 font-semibold text-sm">{item.right}: </span>
                  <span className="text-slate-400 text-sm">{item.desc}</span>
                </div>
              </div>
            ))}
          </div>
          <p>
            To exercise any of these rights, email us at <a href={`mailto:${EMAIL}`} className="text-[#3b82f6] hover:underline">{EMAIL}</a> with the subject line "Privacy Request." We will respond within 30 days. We may need to verify your identity before processing your request.
          </p>
        </Section>

        <Section id="section-12" title="12. California Residents (CCPA)">
          <p>
            If you are a California resident, the California Consumer Privacy Act (CCPA) provides you with specific rights:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong className="text-slate-300">Right to Know:</strong> You may request disclosure of the categories and specific pieces of personal information we have collected about you, the sources, our business purposes, and the third parties with whom we share it.</li>
            <li><strong className="text-slate-300">Right to Delete:</strong> You may request deletion of personal information we have collected, subject to legal exceptions.</li>
            <li><strong className="text-slate-300">Right to Opt Out of Sale:</strong> We do not sell your personal information. You do not need to opt out.</li>
            <li><strong className="text-slate-300">Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA rights.</li>
          </ul>
          <p>
            To submit a CCPA request, contact us at {EMAIL} with "CCPA Request" in the subject line.
          </p>
        </Section>

        <Section id="section-13" title="13. EEA and UK Residents (GDPR)">
          <p>
            If you are located in the European Economic Area or the United Kingdom, the General Data Protection Regulation (GDPR) or UK GDPR may apply to you. Our legal bases for processing your personal data are:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong className="text-slate-300">Contract performance:</strong> Processing necessary to generate and deliver your Blueprint and manage your subscription.</li>
            <li><strong className="text-slate-300">Legitimate interests:</strong> Analytics to improve our service, fraud prevention, and follow-up emails related to your purchase.</li>
            <li><strong className="text-slate-300">Legal obligation:</strong> Retaining payment records for tax compliance.</li>
            <li><strong className="text-slate-300">Consent:</strong> Marketing emails beyond the core post-purchase sequence (you can withdraw consent at any time).</li>
          </ul>
          <p>
            Your data may be transferred to and processed in the United States, where our primary infrastructure is located. We rely on Standard Contractual Clauses or equivalent mechanisms for such transfers where required.
          </p>
          <p>
            You have the right to lodge a complaint with your local data protection authority if you believe we have processed your data unlawfully. We would, however, appreciate the opportunity to address your concerns directly first.
          </p>
        </Section>

        <Section id="section-14" title="14. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. When we do, we will update the effective date at the top of this page. For material changes that affect how we use your data, we will notify active subscribers by email at least 14 days before the change takes effect.
          </p>
          <p>
            Your continued use of our services after the effective date of any updated Privacy Policy constitutes your acceptance of the changes.
          </p>
        </Section>

        <Section id="section-15" title="15. Contact Us">
          <p>
            If you have questions, concerns, or requests related to this Privacy Policy or your personal data, contact our privacy team:
          </p>
          <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl p-5 mt-3">
            <p className="text-white font-semibold">{COMPANY}</p>
            <p className="text-slate-400 mt-1">Email: <a href={`mailto:${EMAIL}`} className="text-[#3b82f6] hover:underline">{EMAIL}</a></p>
            <p className="text-slate-400">Website: <a href={`https://${SITE}`} className="text-[#3b82f6] hover:underline">{SITE}</a></p>
            <p className="text-slate-500 text-xs mt-3">We aim to respond to all privacy inquiries within 5 business days.</p>
          </div>
        </Section>

        {/* Footer note */}
        <div className="mt-12 pt-8 border-t border-white/8 text-center">
          <p className="text-slate-600 text-xs">
            This Privacy Policy was last updated on {EFFECTIVE_DATE}. See also our{" "}
            <Link to="/terms" className="text-[#3b82f6] hover:underline">Terms of Service</Link>.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPage;

